<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="700px"
  >
    <template v-slot:activator="{ on: dialog }">
      <v-tooltip bottom>
        <template v-slot:activator="{ on: tooltip }">
          <v-btn
            style="max-width:36px !important"
            icon
            v-on="{ ...dialog, ...tooltip }"
          >
            <v-icon color="primary">
              mdi-pencil
            </v-icon>
          </v-btn>
        </template>
        <span>Edit entry</span>
      </v-tooltip>
    </template>

    <div v-if="localEntry">
      <material-card
        :offset="10"
        :full-width="true"
        :loading="loading"
        color="hrissecondary"
        title="Claim Request Entry"
        text="Edit Claim Request Entry"
        class="mb-0"
      >
        <form v-if="dialog && !loading">
          <v-container>
            <v-row no-gutters>
              <v-col
                cols="12"
                class="px-2"
              >
                <v-autocomplete
                  v-model="claimType"
                  :items="[entry.claim_type]"
                  item-text="name"
                  item-value="id"
                  disabled
                  return-object
                  label="Claim Type"
                  outlined
                />
              </v-col>
            </v-row>

            <div v-if="claimType">
              <v-row
                v-show="claimType.requires_receipt"
                no-gutters
              >
                <v-col
                  cols="6"
                  class="px-2"
                >
                  <v-menu
                    v-model="receiptDateMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        v-model="receiptDate"
                        v-validate="claimType.requires_receipt ? 'required' : ''"
                        label="Receipt Date"
                        :error-messages="errors.collect('receipt_date')"
                        data-vv-name="receipt_date"  
                        data-vv-as="date"      
                        readonly
                        outlined
                        class="label-position"
                        v-on="on"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon>
                            mdi-calendar
                          </v-icon>
                        </template>
                      </v-text-field>
                    </template>
                    <v-date-picker
                      v-model="receiptDate"
                      :max="maxReceiptDate"
                      :min="minReceiptDate"
                      @input="receiptDateMenu = false"
                    />
                  </v-menu>
                </v-col>

                <v-col
                  cols="6"
                  class="px-2"
                >
                  <v-text-field
                    v-model="receiptNumber"
                    v-validate="claimType.requires_receipt ? 'required' : ''"
                    :error-messages="errors.collect('receipt_no')"
                    data-vv-name="receipt_no"
                    data-vv-as="receipt number"
                    label="Receipt No."
                    outlined
                  />
                </v-col>
              </v-row>

              <v-row
                v-if="claimEntitlement && claimEntitlement.claim_entitlement_settings && claimEntitlement.claim_entitlement_settings.credit_unit === 'month'"
                no-gutters
              >
                <v-col
                  cols="12"
                  class="px-2"
                >
                  <v-menu
                    v-model="claimMonthMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        v-model="claimMonthDisplay"
                        v-validate="'required'"
                        label="Claim for"
                        :error-messages="errors.collect('claimMonthDisplay')"
                        data-vv-name="claimMonthDisplay"  
                        data-vv-as="claim month"      
                        readonly
                        outlined
                        class="label-position"
                        v-on="on"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon>
                            mdi-calendar
                          </v-icon>
                        </template>
                      </v-text-field>
                    </template>
                    <v-date-picker
                      v-model="claimMonth"
                      type="month"
                      :min="claimType.requires_receipt ? receiptDate : ''"
                      :max="!claimType.requires_receipt ? today : ''"
                      @input="claimMonthMenu = false"
                    />
                  </v-menu>
                </v-col>
              </v-row>

              <v-row
                v-if="claimType.details.length > 0"
                no-gutters
              >
                <v-col
                  class="px-2"
                >
                  <v-autocomplete
                    v-model="detail"
                    v-validate="claimType.details.length > 0 ? 'required' : ''"
                    :items="claimType.details"
                    data-vv-name="detail_id"
                    :error-messages="errors.collect('detail_id')"
                    item-text="name"
                    item-value="id"
                    outlined
                    return-object
                    @input="claimDetailChanged(detail)"
                  />     
                </v-col>
              </v-row>

              <v-row
                v-for="field in (claimType.custom_fields && claimType.custom_fields.length > 0 ? claimType.custom_fields : [])"
                :key="field.id"
                no-gutters
              >
                <v-col
                  cols="12"
                  class="px-2"
                >
                  <!-- Text -->
                  <v-text-field
                    v-if="field.type === 'Text'"
                    v-model="customFieldValues[field.id]"
                    v-validate="field.is_mandatory ? 'required' : ''"
                    :data-vv-name="field.name"
                    :data-vv-as="field.name.toLowerCase()"
                    :error-messages="errors.collect(field.name)"
                    :label="field.name"
                    outlined
                  />

                  <!-- Date -->
                  <v-menu
                    v-else-if="field.type === 'Date'"
                    v-model="datePickers[field.id]"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        v-model="customFieldValues[field.id]"
                        v-validate="field.is_mandatory ? 'required' : ''"
                        :data-vv-name="`custom_field_${field.id}`"
                        :data-vv-as="field.name.toLowerCase()"
                        :error-messages="errors.collect(`custom_field_${field.id}`)"
                        :label="field.name"
                        class="label-position"
                        readonly
                        outlined
                        v-on="on"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon>
                            mdi-calendar
                          </v-icon>
                        </template>
                      </v-text-field>
                    </template>
                    <v-date-picker
                      v-model="customFieldValues[field.id]"
                      @input="onDateSelected(field)"
                    />
                  </v-menu>

                  <!-- Selection -->
                  <v-autocomplete
                    v-else-if="field.type === 'Selection'"
                    v-model="customFieldValues[field.id]"
                    v-validate="field.is_mandatory ? 'required' : ''"
                    :items="field.selections"
                    :data-vv-name="field.name"
                    :data-vv-as="field.name.toLowerCase()"
                    :error-messages="errors.collect(field.name)"
                    item-text="name"
                    item-value="id"
                    :label="field.name"
                    outlined
                  />
                  
                  <!-- Number -->
                  <v-text-field
                    v-else-if="field.type === 'Number'"
                    v-model="customFieldValues[field.id]"
                    v-validate="field.is_mandatory ? 'required' : ''"
                    :data-vv-name="field.name"
                    :data-vv-as="field.name.toLowerCase()"
                    :error-messages="errors.collect(field.name)"
                    :label="field.name"
                    type="number"
                    outlined
                    min="0"
                    @keydown.native="['e', 'E', '+'].includes($event.key) && $event.preventDefault()"
                  />
                </v-col>
              </v-row>
            
              <v-row
                v-if="!!claimType.has_limit && claimEntitlement && claimEntitlement.claim_entitlement_settings && claimEntitlement.claim_entitlement_settings.credit_unit === 'day'"
                no-gutters
              >
                <v-col
                  cols="12"
                  class="px-2"
                >
                  <v-menu
                    v-model="dateMenu"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        :value="dateRangeDisplay"
                        label="Claim Period"
                        readonly
                        class="label-position"
                        outlined
                        prepend-inner-icon="mdi-calendar"
                        v-on="on"
                        @click:clear="
                          dates = [];
                          if (claimEntitlement.claim_entitlement_settings.allow_claim_half_entitlement) {
                            quantity = 0
                          }
                        "
                      />
                    </template>
                    <v-date-picker
                      v-model="dates"
                      :min="minReceiptDate"
                      :max="today"
                      range
                      no-title
                      scrollable
                      :allowed-dates="allowedDates"
                    />
                  </v-menu>
                  <v-col
                    v-if="noDateSelected"
                    cols="12"
                    class="mt-n10"
                  >
                    <span
                      class="red--text text--lighten-1 caption"
                    >
                      Please select at least one date.
                    </span>
                  </v-col>
                </v-col>
                <v-col
                  v-if="claimEntitlement.claim_entitlement_settings.allow_claim_half_entitlement && dates.length > 1"
                  cols="12"
                  class="px-2 py-0 mt-n4 mb-3"
                >
                  <v-simple-table 
                    dense 
                    outlined
                  >
                    <thead>
                      <tr>
                        <th class="text-left">
                          Date
                        </th>
                        <th class="text-left">
                          Half?
                        </th>
                        <th class="text-right">
                          Claim Amount (RM)
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      <tr 
                        v-for="(dateClaim, counter) in dateClaims" 
                        :key="counter"
                      >
                        <!-- Date -->
                        <td>{{ dateClaim.date }}</td>

                        <!-- Half entitlement -->
                        <td>
                          <v-checkbox
                            v-model="dateClaim.is_half_entitlement"
                            :name="'is_half_entitlement' + counter"
                            color="green"
                            hide-details
                            class="mt-0 pa-0"
                            @change="recalculateTotals()"
                          />
                        </td>

                        <!-- Amount to deduct -->
                        <td class="text-right">
                          {{ formatMoney(dateClaim.amount_to_deduct) }}
                        </td>
                      </tr>
                    </tbody>

                    <tfoot>
                      <tr>
                        <td
                          colspan="3"
                          class="text-right font-weight-bold"
                        >
                          Total = {{ formatMoney(totalAmount) }}
                        </td>
                      </tr>
                    </tfoot>
                  </v-simple-table>
                </v-col>

                <v-col
                  v-if="claimEntitlement && claimEntitlement.claim_entitlement_settings && claimEntitlement.claim_entitlement_settings.allow_combine_entitlement"
                  cols="12"
                  class="px-2"
                >
                  <v-autocomplete
                    v-model="combineEntitlementEmployees"
                    :error-messages="errors.collect('employees')"
                    :items="departmentEmployees"
                    data-vv-name="employees"
                    data-vv-as="employees"
                    item-text="person.name"
                    item-value="employee_uid"
                    :disabled="dates.length <= 0"
                    label="Combine entitlement with"
                    placeholder="Select colleagues to combine entitlements if any"
                    outlined
                    hide-no-data
                    hide-selected
                    small-chips
                    multiple
                    deletable-chips
                    clearable
                  />
                </v-col>
              </v-row>

              <v-row 
                v-if="!(claimType && !!claimType.has_limit && claimEntitlement && claimEntitlement.claim_entitlement_settings && claimEntitlement.claim_entitlement_settings.credit_unit === 'day' && claimEntitlement.claim_entitlement_settings.allow_claim_half_entitlement)"
                no-gutters
              >
                <v-col
                  cols="4"
                  class="px-2"
                >
                  <v-text-field
                    v-model="quantity"
                    v-validate="'required|checkDecimalPlace|validAmount'"
                    type="number"
                    :error-messages="errors.collect('quantity')"
                    data-vv-name="quantity"
                    data-vv-as="quantity"
                    label="Unit/km"
                    :readonly="claimEntitlement && claimEntitlement.claim_entitlement_settings && claimEntitlement.claim_entitlement_settings.credit_unit === 'day' ? true : false"
                    outlined
                    min="1"
                    @keydown.native="['e', 'E', '+', '-'].includes($event.key) && $event.preventDefault()"
                  />
                </v-col>

                <v-col
                  cols="4"
                  class="px-2"
                >
                  <v-text-field
                    :key="amountKey"
                    v-model="amount"
                    v-money="money"
                    v-validate="'required|validAmount'"
                    :error-messages="errors.collect('amount')"
                    data-vv-as="amount"
                    data-vv-name="amount"
                    label="Amount per unit"
                    prefix="RM"
                    :readonly="!!detail || !!(claimEntitlement && claimEntitlement.claim_entitlement_settings && claimEntitlement.claim_entitlement_settings.is_fixed_amount)"
                    outlined
                    @keydown.native="['-'].includes($event.key) && $event.preventDefault()"
                  />
                </v-col>

                <v-col
                  cols="4"
                  class="px-2"
                >
                  <v-text-field
                    v-money="money"
                    :value="totalAmount"
                    :disabled="true"
                    label="Total Amount"
                    prefix="RM"
                    outlined
                  />

                  <v-col
                    cols="12"
                    class="mt-n10"
                  >
                    <span
                      v-if="isExceedLimit && !loading"
                      class="red--text text--lighten-1 caption"
                    >
                      {{ limitCheckingMessage }}
                    </span>
                  </v-col>
                </v-col>
              </v-row>

              <v-row no-gutters>
                <v-col class="px-2">
                  <v-textarea
                    v-model="description"
                    v-validate="claimType.is_description_mandatory ? 'required' : ''"
                    :error-messages="errors.collect('description')"
                    data-vv-name="description"
                    data-vv-as="description"
                    label="Description"
                    outlined
                    rows="3"
                    no-resize
                  />
                </v-col>
              </v-row>

              <v-row
                v-show="claimType.requires_document"
                no-gutters
              >
                <v-col cols="12">
                  <v-btn
                    :disabled="progressing"
                    class="mx-2"
                    @click="$refs.fileInput.click()"
                  >
                    <v-icon 
                      small
                      left
                    >
                      mdi-paperclip
                    </v-icon>
                    Add Files
                  </v-btn>
                  <input
                    ref="fileInput"
                    type="file"
                    multiple
                    class="d-none"
                    accept=".jpg,.jpeg,.png,.pdf"
                    @change="onFilesSelected"
                  >
                  <input
                    v-validate="claimType.requires_document ? 'attachmentRequired' : ''"
                    type="hidden"
                    :value="attachments.length || existingAttachments.length"
                    data-vv-name="file"
                  >
                  <!-- list existing attachments -->
                  <div 
                    v-if="existingAttachments.length"
                    class="px-4"
                  >
                    <v-row
                      v-for="(file, index) in existingAttachments"
                      :key="'existing-' + index"
                    >
                      <v-chip
                        class="mt-2"
                        small
                        close
                        @click:close="markFileForDeletion(index)"
                        @click="previewExistingFile(file)"
                      >
                        {{ file.name }}
                      </v-chip>
                    </v-row>
                  </div>
                  <!-- list new added attachments -->
                  <div
                    v-if="attachments.length"
                    class="px-4"
                  >
                    <v-row
                      v-for="(file, index) in attachments"
                      :key="'new-' + index"
                    >
                      <v-chip
                        class="mt-2"
                        small
                        close
                        @click:close="removeFile(index)"
                        @click="previewFile(file)"
                      >
                        {{ file.name }}
                      </v-chip>
                    </v-row>
                  </div>
                  <div
                    v-if="!attachments.length && !existingAttachments.length"
                    class="px-4"
                  >
                    <span>No file chosen (.pdf, .jpg, .jpeg, .png)</span>
                  </div>

                  <div
                    v-if="uploadErrors.length"
                    class="px-2"
                  >
                    <span
                      v-for="(err, i) in uploadErrors"
                      :key="i"
                      class="red--text text-caption d-block"
                    >
                      {{ err }}
                    </span>
                  </div>
                  <span class="red--text text--lighten-1 caption px-4">{{ errors.first('file') }}</span>
                </v-col>
              </v-row>

              <v-row
                v-show="claimType.requires_declaration"
                no-gutters
              >
                <div class="d-flex align-start">
                  <v-checkbox
                    v-model="localEntry.declaration_accepted"
                    color="green"
                    hide-details
                    class="mt-0 pa-0"
                  />
                  <span
                    v-html="claimType.declaration_text"
                  />
                </div>
              </v-row>
            </div>
          </v-container>
        </form>

        <v-card-actions>
          <v-spacer />
          <v-btn
            :disabled="progressing"
            text
            class="px-10"
            @click="closeDialog"
          >
            Cancel
          </v-btn>
          <v-btn
            :loading="progressing"
            :disabled="loading || !claimType || isExceedLimit || (claimType && !!claimType.requires_declaration && !localEntry.declaration_accepted) || (claimType && !!claimType.has_limit && claimEntitlement && claimEntitlement.claim_entitlement_settings && claimEntitlement.claim_entitlement_settings.credit_unit === 'day' && noDateSelected ? true : false)"
            color="hrissecondary"
            class="px-10 white--text"        
            @click="submitForm"
          >
            Save
          </v-btn>
        </v-card-actions>
      </material-card>
    </div>
  </v-dialog>
</template>

<script>
  import { VMoney } from 'v-money'
  import { mapState, mapGetters } from 'vuex'
  export default {
    directives: { money: VMoney },
    $_veeValidate: {
      validator: 'new', 
    },
    props: {
      entry: {
        type: Object,
        required: true,
      },
      claimEntitlements: {
        type: Array,
        required: true,
      },
      claimRequest: {
        type: Object,
        required: true,
      },
      transactionDateValidity: {
        type: Array,
        required: true,
      },
    },
    data () {
      return {
        localEntry: null,
        dialog: false,
        loading: false,
        progressing: false,
        claimType: null,
        receiptDateMenu: false,
        receiptDate: null,
        receiptNumber: null,
        quantity: 1,
        money: {
          decimal: '.',
          thousands: ',',
          precision: 2,
          masked: false,
        },
        amountKey: 0,
        amount: 0,
        description: null,
        customFieldValues: {},
        datePickers: {},
        detail: null,
        limit: 0,
        noDateSelected: false,
        dateMenu: false,
        dates: [],
        dateClaims: [],
        today: new Date().toISOString().slice(0, 10),
        claimEntitlement: null,
        attachments: [],
        existingAttachments: [],
        filesToDelete: [],
        uploadErrors: [],
        allowedTypes: [
          'image/jpeg',
          'image/png',
          'application/pdf',
        ],
        maxFiles: 5,
        claimMonthMenu: false,
        claimMonth: null,
        includesEndDate: true,
        departmentEmployees: [],
        combineEntitlementEmployees: [],
      }
    },
    computed: {
      ...mapGetters(['errors']),
      ...mapState(['currentEmployee']),
      maxReceiptDate () {
        const cycleEndDate = this.$moment(this.claimRequest.claim_cycle.end_date).format('YYYY-MM-DD')

        if (this.$moment().isAfter(cycleEndDate)) {
          return cycleEndDate
        } else {
          if (this.claimRequest.request_date) {
            return this.$moment(this.claimRequest.request_date).format('YYYY-MM-DD')
          } else {
            return this.$moment().format('YYYY-MM-DD')
          }
        }
      },
      minReceiptDate () {
        const cycleEndDate = this.$moment(this.claimRequest.claim_cycle.end_date).format('YYYY-MM-DD')
        const lastEndDay = this.transactionDateValidity[this.transactionDateValidity.length - 1].end_day

        if (this.$moment().isAfter(cycleEndDate)) {
          return this.$moment(this.claimRequest.claim_cycle.end_date)
            .subtract(lastEndDay, 'days')
            .format('YYYY-MM-DD')
        } else {
          if (this.claimRequest.request_date) {
            return this.$moment(this.claimRequest.request_date)
              .subtract(lastEndDay, 'days')
              .format('YYYY-MM-DD')
          } else {
            return this.$moment()
              .subtract(lastEndDay, 'days')
              .format('YYYY-MM-DD')
          }
        }
      },
      isExceedLimit () {
        if (
          this.claimType && 
          !!this.claimType.has_limit &&
          !this.claimType.allow_exceed_limit && 
          this.claimEntitlement &&
          (
            this.claimEntitlement.claim_entitlement_settings.credit_unit === 'year' ||
            (this.claimEntitlement.claim_entitlement_settings.credit_unit === 'day' && this.claimEntitlement.claim_entitlement_settings.allow_combine_entitlement && this.dates.length > 1) ||
            (this.claimEntitlement.claim_entitlement_settings.credit_unit === 'month' && this.claimMonth)
          )
        ) {
          const parsedTotal = parseFloat(this.totalAmount.toString().replace(/,/g, ''))
          const roundedAmount = Math.round(parsedTotal * 100)
          return roundedAmount > this.limit
        }

        return false
      },
      totalAmount () {
        const cleanAmount = parseFloat(this.amount.toString().replace(/,/g, '')) || 0
        const cleanQuantity = parseFloat(this.quantity) || 0
        return parseFloat(cleanAmount * cleanQuantity).toFixed(2)
      },
      limitCheckingMessage () {
        if (this.limit <= 0) return 'Insufficient balance or no entitlement'
        return `Do not exceed RM ${this.thousandSeparator((this.limit / 100).toFixed(2))}`
      },
      claimMonthDisplay () {
        return this.claimMonth ? this.$moment(this.claimMonth + '-01', 'YYYY-MM-DD').format('MMM YYYY') : ''
      },
      dateRangeDisplay () {
        if (Array.isArray(this.dates) && this.dates.length === 2) {
          const [start, end] = this.dates
          return `${start} - ${end}`
        } else {
          return ''
        }
      },
    },
    watch: {
      dialog (val) {
        if (val) {
          this.localEntry = this.entry
          if (this.localEntry) {
            this.initializeForm(this.localEntry)
          }
        }
      },
      receiptDate (receipt) {
        if (
          this.claimType && this.claimEntitlement && this.claimEntitlement.claim_entitlement_settings && this.claimEntitlement.claim_entitlement_settings.credit_unit !== 'day' && 
          !(this.claimEntitlement.claim_entitlement_settings.credit_unit === 'month' && this.claimType.allow_advance_purchase)
        ) {
          const matchingEntitlement = this.claimEntitlement.claim_entitlement_settings.claim_entitlements.find(entitlement => {
            const startDate = new Date(entitlement.start_date)
            const endDate = new Date(entitlement.end_date)
            return startDate <= new Date(receipt) && new Date(receipt) <= endDate
          })

          if (matchingEntitlement) {
            this.limit = (matchingEntitlement.current_available - matchingEntitlement.pending_amt - matchingEntitlement.claimed_amount) + this.entry.total_amount + matchingEntitlement.adjusted_amount
          }
        }
      },
      claimMonth (month) {
        this.limit = 0
        const monthSelected = month + '-01'

        if (
          this.claimType && this.claimEntitlement && this.claimEntitlement.claim_entitlement_settings && 
          this.claimEntitlement.claim_entitlement_settings.credit_unit === 'month'
        ) {
          const matchingEntitlement = this.claimEntitlement.claim_entitlement_settings.claim_entitlements.find(entitlement => {
            return entitlement.start_date === monthSelected
          })

          if (matchingEntitlement) {
            if (matchingEntitlement.start_date === this.entry.entitlement_date_to_deduct) {
              this.limit = (matchingEntitlement.current_available - matchingEntitlement.pending_amt - matchingEntitlement.claimed_amount) + this.entry.total_amount + matchingEntitlement.adjusted_amount
            } else {
              this.limit = (matchingEntitlement.current_available - matchingEntitlement.pending_amt - matchingEntitlement.claimed_amount) + matchingEntitlement.adjusted_amount
            }
          }
        }
      },
      dates (newDates) {
        if (!newDates || newDates.length === 0) {
          this.dateClaims = []
          this.quantity = 0
          this.noDateSelected = true
          return
        }
        
        if (this.claimEntitlement && this.claimEntitlement.claim_entitlement_settings && this.claimEntitlement.claim_entitlement_settings.allow_claim_half_entitlement) {
          const datesInRange = this.getAllDatesInRange()
          const maxAmount = this.claimEntitlement.claim_entitlement_settings.claim_entitlement_setting_details[0].max_amount / 100
          
          const updated = datesInRange.map(date => {
            const existing = this.dateClaims.find(dc => dc.date === date)
            return existing || {
              date,
              base_amount: maxAmount,
              amount_to_deduct: maxAmount,
              is_half_entitlement: false,
            }
          })

          this.dateClaims = updated
          this.recalculateTotals()
        }

        if (this.claimEntitlement && this.claimEntitlement.claim_entitlement_settings && this.claimEntitlement.claim_entitlement_settings.allow_combine_entitlement) {
          this.getEntitlementBalance(this.combineEntitlementEmployees)
        }
      },
      combineEntitlementEmployees (employees) {
        if (!this.dates || this.dates.length === 0) return

        // Clear previous timeout if still waiting
        clearTimeout(this._combineEntitlementTimeout)

        this._combineEntitlementTimeout = setTimeout(() => {
          if (
            this.claimType && this.claimType.has_limit && !this.claimType.allow_exceed_limit &&
            this.claimEntitlement.claim_entitlement_settings.credit_unit === 'day' &&
            this.claimEntitlement.claim_entitlement_settings.allow_combine_entitlement
          ) {
            this.getEntitlementBalance(employees)
          }
        }, 1000) // 1 second
      },
    },
    created () {
      this.$validator.extend('validAmount', {
        getMessage (field, args) {
          return 'Should more than 0'     
        },
        validate (value, args) {
          return value.toString().replaceAll(',', '') > 0
        },
      })
      this.$validator.extend('checkDecimalPlace', {
        getMessage (field) {
          return 'Only allow up to 3 decimal places'
        },
        validate (value) {
          const cleanValue = value.toString().replace(/,/g, '')
          const regex = /^\d+(\.\d{1,3})?$/

          return regex.test(cleanValue)
        },
      })
      this.$validator.extend('attachmentRequired', {
        getMessage (field) {
          return 'Please provide at least one supporting document'
        },
        validate (value) {
          return Number(value) > 0
        },
      })
    },
    methods: {
      getEntitlementBalance (employees) {
        if (this.dates.length > 1) {
          this.loading = true
          this.$api.claimEntitlements().getTotalEntitlements(this.prepareGetTotalEntitlementFormData(employees))
            .then(res => {
              this.limit = res.data
              this.loading = false
            })
        }
      },
      initializeForm (value) {
        this.claimType = value.claim_type
        this.amount = value.amount
        this.quantity = value.quantity

        this.detail = value.claim_type_detail

        if (
          value.claim_request_custom_field_entries.length > 0 &&
          this.claimType &&
          this.claimType.custom_fields.length > 0
        ) {
          value.claim_request_custom_field_entries.forEach(entry => {
            const fieldId = entry.claim_type_custom_field_id
            const field = this.claimType.custom_fields.find(f => f.id === fieldId)
            if (field) {
              let fieldValue = null
              if (field.type === 'Selection') {
                fieldValue = entry.claim_type_custom_field_selection.id
              } else {
                fieldValue = entry.value
              }

              this.$set(this.customFieldValues, fieldId, fieldValue)
            }
          })
        }

        if (value.receipt_date) {
          this.receiptDate = value.receipt_date
        }

        if (value.entitlement_date_to_deduct) {
          this.claimMonth = this.$moment(value.entitlement_date_to_deduct, 'YYYY-MM-DD').format('YYYY-MM')
        }

        if (value.receipt_number) {
          this.receiptNumber = value.receipt_number
        }

        if (value.description) {
          this.description = value.description.replace(/^.*?\.<\/br>/i, '')
        }

        if (value.documents.length > 0) {
          this.existingAttachments = value.documents.map(file => ({
            id: file.id,
            name: file.file_name,
            url: file.file_url,
          }))
        }

        if (value.claim_request_entry_dates.length > 0) {
          const sortedDates = value.claim_request_entry_dates.map(entry => entry.date).sort((a, b) => new Date(a) - new Date(b))

          const earliest = sortedDates[0]
          const latest = sortedDates[sortedDates.length - 1]

          this.dates = [earliest, latest]

          if ((this.claimEntitlement && this.claimEntitlement.claim_entitlement_settings && this.claimEntitlement.claim_entitlement_settings.allow_claim_half_entitlement) || value.claim_request_entry_dates[0].amount_to_deduct) {
            this.amount = value.amount / 100

            this.dateClaims = value.claim_request_entry_dates.map(entry => {
              const baseAmount = this.amount
              const amountToDeduct = entry.amount_to_deduct / 100
              const isHalf = Math.abs(amountToDeduct - baseAmount / 2) < 0.01

              return {
                date: entry.date,
                base_amount: baseAmount,
                amount_to_deduct: amountToDeduct,
                is_half_entitlement: isHalf,
              }
            })
          }
        }

        if (value.claim_request_entry_employees.length > 0) {
          if (this.departmentEmployees.length <= 0) {
            this.loadEmployees()
          }

          this.combineEntitlementEmployees = value.claim_request_entry_employees.map(entry => entry.employee_uid)
        }

        if (this.claimType) {
          this.claimEntitlement = this.claimEntitlements.find(entitlement => {
            return entitlement.id === this.claimType.id
          })

          if (this.claimEntitlement && this.claimEntitlement.claim_entitlement_settings) {
            if (this.claimEntitlement.claim_entitlement_settings.credit_unit === 'day') {
              if (this.claimEntitlement.claim_entitlement_settings.allow_combine_entitlement && this.combineEntitlementEmployees.length > 0) {
                this.limit = this.amount
              } else {
                this.limit = this.claimEntitlement.claim_entitlement_settings.claim_entitlements
                  .filter(e => this.dates.includes(e.start_date))
                  .reduce((total, e) => total + e.balance, 0)
              }

              if (this.dates.length <= 0) {
                this.noDateSelected = true
              } else {
                this.noDateSelected = false
              }

              if (this.claimEntitlement.claim_entitlement_settings.allow_claim_half_entitlement) {
                this.recalculateTotals()
              }

              if (this.claimEntitlement.claim_entitlement_settings.allow_combine_entitlement && this.departmentEmployees.length <= 0) {
                this.loadEmployees()
              }

              if (this.claimEntitlement.claim_entitlement_settings.includes_end_date) {
                this.includesEndDate = true
              } else {
                this.includesEndDate = false
              }
            }

            if (!this.claimType.requires_receipt && !!this.claimType.has_limit && this.claimEntitlement.claim_entitlement_settings.credit_unit !== 'day') {
              const claimCycleStartDate = new Date(this.claimRequest.claim_cycle.start_date)
              const claimCycleEndDate = new Date(this.claimRequest.claim_cycle.end_date)

              const sortedEntitlements = this.claimEntitlement.claim_entitlement_settings.claim_entitlements
                .slice()
                .sort((a, b) => new Date(a.start_date) - new Date(b.start_date))

              const matchingEntitlement = sortedEntitlements.find(entitlement => {
                const startDate = new Date(entitlement.start_date)
                const endDate = new Date(entitlement.end_date)
                return startDate <= claimCycleEndDate && claimCycleStartDate <= endDate
              })

              if (matchingEntitlement) {
                this.limit = (matchingEntitlement.current_available - matchingEntitlement.pending_amt - matchingEntitlement.claimed_amount) + this.entry.total_amount + matchingEntitlement.adjusted_amount
              }
            }

            if (
              this.claimType.allow_advance_purchase && this.claimType.requires_receipt &&
              this.claimEntitlement.claim_entitlement_settings.credit_unit === 'month'
            ) {
              const matchingEntitlement = this.claimEntitlement.claim_entitlement_settings.claim_entitlements.find(entitlement => {
                const startDate = new Date(entitlement.start_date)
                const endDate = new Date(entitlement.end_date)
                return startDate === new Date(this.entry.entitlement_date_to_deduct) && new Date(this.entry.entitlement_date_to_deduct) <= endDate
              })

              if (matchingEntitlement) {
                this.limit = (matchingEntitlement.current_available - matchingEntitlement.pending_amt - matchingEntitlement.claimed_amount) + this.entry.total_amount + matchingEntitlement.adjusted_amount
              }
            }
          }
        }
      },
      loadEmployees () {
        this.loading = true
        this.$api.hrisEmployees().list('?claimRequestId=' + this.claimRequest.id)
          .then((res) => {
            this.departmentEmployees = _.orderBy(res.data, ['person.name'], ['asc'])

            this.loading = false
          })
      },
      prepareGetTotalEntitlementFormData (employees) {
        let allDates = this.getAllDatesInRange()

        if (
          this.claimType && this.claimEntitlement && this.claimEntitlement.claim_entitlement_settings && 
          this.claimEntitlement.claim_entitlement_settings.credit_unit === 'day' &&
          !this.claimEntitlement.claim_entitlement_settings.includes_end_date &&
          allDates.length > 1
        ) {
          allDates = allDates.slice(0, -1)
        }

        return {
          employees: employees,
          claim_type_id: this.claimType.id,
          dates: allDates,
        }
      },
      formatMoney (value) {
        if (!value) return 'RM 0.00'
        // v-money-like formatting
        return Number(value).toLocaleString('en-MY', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      },
      recalculateTotals () {
        // adjust amount_to_deduct based on half/full
        this.dateClaims.forEach(c => {
          c.amount_to_deduct = c.is_half_entitlement
            ? c.base_amount / 2
            : c.base_amount
        })

        // quantity = sum of full (1) + half (0.5)
        this.quantity = this.dateClaims.reduce(
          (sum, c) => sum + (c.is_half_entitlement ? 0.5 : 1),
          0
        )
      },
      getAllDatesInRange () {
        const [start, end] = this.dates
        if (!start || !end) return []

        const startDate = this.$moment(start)
        const endDate = this.$moment(end)
        const dateList = []

        while (startDate.isSameOrBefore(endDate)) {
          dateList.push(startDate.format('YYYY-MM-DD'))
          startDate.add(1, 'day')
        }

        return dateList
      },
      allowedDates (date) {
        // if only the first date (start) is selected
        if (this.dates.length === 1) {
          const start = this.dates[0]
          // disable any date *before* the start
          return date >= start
        }
        // otherwise allow all dates (default)
        return true
      },
      closeDialog () {
        this.$validator.reset()
        this.limit = 0
        this.description = null
        this.receiptDateMenu = false
        this.receiptDate = null
        this.attachments = []
        this.uploadErrors = []
        this.dialog = false
      },
      claimDetailChanged (obj) {
        if (obj && obj.amount) {
          const rawAmount = obj.amount / 100

          this.amount = rawAmount.toFixed(2)
          this.amountKey++
        }
      },
      submitForm () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return

            this.progressing = true

            this.$api.claimRequestEntries().update(this.entry.id, this.prepareFormData())
              .then((res) => {
                this.$emit('updated')
                this.$snack({ message: res.data.status })

                this.closeDialog()
              })
              .catch(error => {
                if (error.response.status === 500) {
                  this.$snack({ 
                    color: 'error',
                    message: 'A server error occurred. Please try again later', 
                  })
                } else {
                  this.$snack({ 
                    color: 'error',
                    message: 'Error! Please contact IT support', 
                  })
                }
              })
              .finally(() => {
                this.progressing = false
              })
          })
      },
      prepareFormData () {
        const { newFiles, deleteFileIds } = this.getPayloadForSubmit()
        let formData = new FormData()
        const datesInRange = this.getAllDatesInRange()

        formData.append('includes_end_date', this.includesEndDate)
        formData.append('quantity', this.quantity)
        formData.append('amount', (this.amount.toString().replace(/,/g, '') * 100))
        formData.append('claim_entitlement_dates', JSON.stringify(datesInRange))
        formData.append('claim_entitlement_dates_with_amount', JSON.stringify(this.dateClaims))
        formData.append('combine_with', JSON.stringify(this.combineEntitlementEmployees))

        if (this.claimType) {
          if (this.claimType.requires_receipt) {
            formData.append('receipt_date', this.receiptDate)
            formData.append('receipt_number', this.receiptNumber)
          }

          if (this.claimEntitlement && this.claimEntitlement.claim_entitlement_settings && this.claimEntitlement.claim_entitlement_settings.credit_unit === 'month') {
            formData.append('entitlement_date_to_deduct', this.claimMonth + '-01')
          }
          
          if (this.claimType.requires_declaration) {
            formData.append('declaration_accepted', this.localEntry.declaration_accepted ? 1 : 0)
          }
        }

        if (this.detail) {
          formData.append('claim_type_detail_id', this.detail.id)
        }

        if (this.description) {
          if (this.detail) {
            formData.append('description', 'Claim for ' + this.detail.name + '.</br>' + this.description)
          } else {
            formData.append('description', this.description)
          }
        }
        
        if (this.customFieldValues) {
          formData.append(
            'custom_fields', 
            JSON.stringify(
              Object.entries(this.customFieldValues).map(([id, value]) => ({
                id: Number(id),
                value,
              }))
            )
          )
        }

        // add new files to FormData
        newFiles.forEach(file => {
          formData.append('new_files[]', file)
        })

        // deleted file IDs
        deleteFileIds.forEach(id => {
          formData.append('deleted_file_ids[]', id)
        })

        return formData
      },
      onDateSelected (field) {
        this.datePickers[field.id] = false
        this.$nextTick(() => {
          this.$validator.validate(`custom_field_${field.id}`)
        })
      },
      thousandSeparator (amount) {
        if (amount !== '' || amount !== undefined || amount !== 0 || amount !== '0' || amount !== null) {
          return amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
        } else {
          return amount
        }
      },
      onFilesSelected (e) {
        const files = Array.from(e.target.files)
        let newFiles = []
        this.uploadErrors = []

        for (const file of files) {
          // check invalid file type
          if (!this.allowedTypes.includes(file.type)) {
            this.uploadErrors.push(`Invalid file type: ${file.name}`)
            continue
          }

          // check file limit
          if (this.attachments.length + this.existingAttachments.length + newFiles.length >= this.maxFiles) {
            this.uploadErrors.push(`You can only upload up to ${this.maxFiles} files.`)
            break
          }

          file.preview = URL.createObjectURL(file)
          newFiles.push(file)
        }

        if (newFiles.length) {
          this.attachments.push(...newFiles)
        }

        // reset native file input so user can re-select same file(s) later after being removed
        e.target.value = ''

        this.validateFile()
      },
      removeFile (index) {
        const file = this.attachments[index]
        if (file.preview) {
          URL.revokeObjectURL(file.preview) // cleanup
        }
        this.attachments.splice(index, 1)
        this.uploadErrors = []
        
        this.validateFile()
      },
      markFileForDeletion (index) {
        const file = this.existingAttachments[index]
        this.filesToDelete.push(file.id)
        this.existingAttachments.splice(index, 1)

        this.validateFile()
      },
      validateFile () {
        if (this.claimType && this.claimType.requires_document) {
          if (this.attachments.length + this.existingAttachments.length > 0) {
            this.errors.remove('file')
          } else {
            this.$validator.validate('file')
          }
        }
      },
      previewFile (file) {
        if (file.preview) {
          window.open(file.preview, '_blank')
        }
      },
      previewExistingFile (file) {
        if (file.url) {
          window.open(file.url, '_blank')
        }
      },
      getPayloadForSubmit () {
        return {
          newFiles: this.attachments,
          deleteFileIds: this.filesToDelete,
        }
      },
    },
  }
</script>

<style lang="css" scoped>
.card-overflow {
  height: 40vh;
  overflow-y: scroll;
}
::-webkit-scrollbar {
  width: 0px;  /* Remove scrollbar space */
  background: transparent;  /* Optional: just make scrollbar invisible */
}
.label-position >>> .v-label.v-label--active{
  margin-left:-28px;
  background:white;
  padding-right:5px
}
</style>
